import { observer, useLocalObservable } from 'mobx-react';
import { useRef, useEffect, useCallback } from 'react';
import IconButton, { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import VoiceInputButton from './VoiceInputButton';
import { AGENT_INPUT_LAYOUT } from '../../../utils/constants/agent';
import { isAgentDocumentUploadDisabled } from '../../../utils/constants/variables';
import { htmlToMarkdownLinks } from '../../../utils/htmlToMarkdownLinks';

// TD-1794: ChatGPT approach for flicker-free mode switching
// Only collapse to single-line when input is COMPLETELY EMPTY
// This eliminates all flicker - no need for debouncing or pre-measurement

const {
  SINGLE_LINE_HEIGHT_THRESHOLD,
  CONTAINER_PADDING,
  CONTAINER_BORDER,
  BUTTON_ROW_HEIGHT_DEFAULT
} = AGENT_INPUT_LAYOUT;

/**
 * Calculates whether input should be in multiline mode.
 * TD-1829: Extracted to shared function to eliminate duplication.
 *
 * @param {string} text - Current text content
 * @param {number} height - Current textarea scrollHeight in pixels
 * @param {boolean} currentState - Current multiline state
 * @returns {boolean} Whether input should be in multiline mode
 */
const calculateMultilineState = (text, height, currentState) => {
  const isTrulyEmpty = !text;
  const exceedsLineHeight = height > SINGLE_LINE_HEIGHT_THRESHOLD;

  if (isTrulyEmpty) return false;
  if (text.includes('\n') || exceedsLineHeight) return true;
  return currentState; // Preserve current mode if text exists but fits
};

const AgentUserMessageInput = observer(
  ({
    onSubmit = () => {},
    onUpload = () => {},
    onStop = () => {}, // TD-2347: stop the in-flight agent turn
    running = false, // TD-2347: a task is in progress → show Stop instead of Send
    onHeightChange = null, // TD-1730: Callback when input height changes
    placeholder = 'Type your message here...',
    disabled = false,
    autoFocus = false
  }) => {
    const textareaRef = useRef(null);
    const wrapperRef = useRef(null); // TD-1730: Ref for setting CSS variable

    const state = useLocalObservable(() => ({
      message: '',
      isMultiline: false, // TD-1730: Track if textarea has multiple lines
      setMessage: (value) => {
        state.message = value;
      },
      setMultiline: (value) => {
        state.isMultiline = value;
      }
    }));

    // State to track interim speech input
    const speechState = useLocalObservable(() => ({
      interimText: '',
      isListening: false,
      setInterimText: (text) => {
        speechState.interimText = text;
      },
      setListening: (listening) => {
        speechState.isListening = listening;
      }
    }));

    const handleVoiceTranscript = (transcript) => {
      // Append transcript to existing message
      const currentMessage = state.message;
      const newMessage = currentMessage + (currentMessage ? ' ' : '') + transcript;
      state.setMessage(newMessage);

      // Clear interim text when we get final result
      speechState.setInterimText('');
    };

    const handleInterimTranscript = (interimText) => {
      speechState.setInterimText(interimText);
    };

    const handleSpeechStateChange = (isListening) => {
      speechState.setListening(isListening);
      if (!isListening) {
        speechState.setInterimText('');
      }
    };

    // Calculate display value (existing message + interim text)
    const getDisplayValue = () => {
      const baseMessage = state.message;
      if (speechState.interimText && speechState.isListening) {
        return baseMessage + (baseMessage ? ' ' : '') + speechState.interimText;
      }
      return baseMessage;
    };

    // Auto-resize textarea and detect multiline state
    // TD-1730: Simple approach - only layout transition is animated (like ChatGPT)
    // Textarea height changes instantly, no complex scroll anchoring needed
    const adjustHeight = useCallback(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        const maxHeight = 364; // ~14 lines

        // Measure and set textarea height instantly (no animation)
        textarea.style.height = 'auto';
        const newHeight = Math.min(Math.max(textarea.scrollHeight, 26), maxHeight);
        textarea.style.height = newHeight + 'px';

        // TD-1829: Calculate multiline state FIRST (before height calculation)
        // This ensures correct height is reported when clearing text
        const willBeMultiline = calculateMultilineState(
          state.message,
          newHeight,
          state.isMultiline
        );

        // TD-1730: Calculate total input container height (textarea + padding + border)
        // TD-1829: In multiline mode, buttons move to second row
        const buttonsRowHeight = willBeMultiline ? BUTTON_ROW_HEIGHT_DEFAULT : 0;
        const totalHeight = newHeight + CONTAINER_PADDING + CONTAINER_BORDER + buttonsRowHeight;

        // TD-1730: Call height change callback - parent sets CSS variable on layout root
        // This enables sidebar height calculation via --agent-input-height
        if (onHeightChange) {
          onHeightChange(totalHeight);
        }

        // Update multiline state (for CSS class toggle)
        state.setMultiline(willBeMultiline);
      }
    }, [state, onHeightChange]);

    useEffect(() => {
      adjustHeight();
    }, [state.message, speechState.interimText]);

    // Auto focus on mount if enabled
    useEffect(() => {
      if (autoFocus && textareaRef.current && !disabled) {
        textareaRef.current.focus();
      }
    }, [autoFocus, disabled]);

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        if (e.shiftKey) {
          // Shift+Enter: Insert new line (default behavior)
          return;
        } else {
          // Enter: Submit message
          e.preventDefault();
          handleSubmit();
        }
      }
    };

    const handleSubmit = () => {
      if (state.message.trim() && !disabled) {
        onSubmit(state.message.trim());
        state.setMessage('');
      }
    };

    const handleInputChange = (e) => {
      const newValue = e.target.value;

      // For now, let's simplify this to avoid conflicts
      // Only update if user is actually typing (not during speech recognition state changes)
      if (!speechState.isListening) {
        state.setMessage(newValue);
      }
    };

    // TD-2173: Convert pasted hyperlinks into markdown so URLs survive the
    // textarea (which otherwise consumes only text/plain and drops the href).
    const handlePaste = (e) => {
      // Mirror handleInputChange: ignore manual edits while dictation is active.
      // The textarea shows base + interim speech text, so selectionStart/End
      // here would not line up with state.message and the splice would land at
      // the wrong offset.
      if (speechState.isListening) return;

      const html = e.clipboardData?.getData('text/html');
      const markdown = htmlToMarkdownLinks(html);
      if (markdown === null) return;

      e.preventDefault();
      const textarea = textareaRef.current;
      const start = textarea?.selectionStart ?? state.message.length;
      const end = textarea?.selectionEnd ?? state.message.length;
      const next =
        state.message.slice(0, start) + markdown + state.message.slice(end);
      state.setMessage(next);

      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        const pos = start + markdown.length;
        node.selectionStart = pos;
        node.selectionEnd = pos;
      });
    };

    // Handle container click to focus textarea
    const handleContainerClick = (e) => {
      // Only focus if clicking on the container itself, not on buttons or textarea
      if (e.target === e.currentTarget && textareaRef.current && !disabled) {
        textareaRef.current.focus();
      }
    };

    // TD-1730: Dynamic layout - horizontal (single line) or vertical (multiline)
    // Adds 'multiline' class when textarea grows beyond single line, with CSS animation
    return (
      <div className="agent-user-message-input" ref={wrapperRef}>
        <div
          className={`agent-input-container ${disabled ? 'disabled' : ''} ${state.isMultiline ? 'multiline' : ''} ${isAgentDocumentUploadDisabled ? 'no-upload' : ''}`}
          onClick={handleContainerClick}
        >
          {/* Upload button (left in single-line mode) */}
          {!isAgentDocumentUploadDisabled && (
            <div className="agent-input-upload">
              <IconButton
                icon={ICON_BUTTON_ICONS.UPLOAD_DOCUMENT}
                onClick={onUpload}
                withFill={false}
                withBorder={false}
                disabled={disabled}
                tooltipText="Upload document"
                tooltipPosition="top"
              />
            </div>
          )}

          {/* Textarea - height changes instantly, layout transition animated via CSS Grid */}
          <textarea
            ref={textareaRef}
            className={`agent-input-textarea ${speechState.isListening ? 'agent-input-listening' : ''}`}
            value={getDisplayValue()}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={speechState.isListening ? 'Listening...' : placeholder}
            disabled={disabled}
            rows={1}
          />

          {/* Action buttons (right in single-line mode) */}
          <div className="agent-input-actions">
            <VoiceInputButton
              onTranscript={handleVoiceTranscript}
              onInterimTranscript={handleInterimTranscript}
              onSpeechStateChange={handleSpeechStateChange}
              disabled={disabled}
            />

            {running ? (
              <IconButton
                icon={ICON_BUTTON_ICONS.STOP}
                onClick={onStop}
                withFill={false}
                withBorder={false}
                tooltipText="Stop"
                tooltipPosition="top"
                wrapClassName="agent-stop-button"
              />
            ) : (
              <IconButton
                icon={ICON_BUTTON_ICONS.SEND_CHAT}
                onClick={handleSubmit}
                withFill={false}
                withBorder={false}
                disabled={disabled || !state.message.trim()}
                tooltipText="Send message"
                tooltipPosition="top"
                wrapClassName="agent-send-button"
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default AgentUserMessageInput;
